<script setup>
import { computed, ref, watch } from "vue";
import { usePlanStore } from "@/stores/usePlanStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  planId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

const planStore = usePlanStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const plan = computed(() => {
  if (
    planStore.selectedPlan &&
    planStore.selectedPlan.planId === props.planId
  ) {
    return planStore.selectedPlan;
  }
  return planStore.plans.find((p) => p.planId === props.planId) || null;
});

watch(
  () => props.planId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      await planStore.getPlanById(newId);
    } catch (e) {
      console.error("Erro ao carregar plano no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

const formattedPrice = computed(() => {
  if (!plan.value) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(plan.value.price);
});
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="plan-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes do Plano</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="plan && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ toTitleCase(plan.name) }}
              <span class="title-id">(Id: {{ plan.planId }})</span>
            </h3>
            <span class="price-badge">{{ formattedPrice }}</span>
          </div>

          <div v-if="plan.description" class="description-block">
            <span class="meta-label">Descrição</span>
            <p class="description-text">{{ plan.description }}</p>
          </div>

          <div v-if="plan.mpPlanId" class="mp-plan-block">
            <span class="meta-label">MP Plan ID</span>
            <span class="mp-plan-value" :title="plan.mpPlanId">
              {{ plan.mpPlanId }}
            </span>
          </div>

          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Ciclo</span>
              <span class="meta-value">{{ plan.billingCycle }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Trial</span>
              <span class="meta-value">{{ plan.trialDays }} dias</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Máx. Usuários</span>
              <span class="meta-value">{{ plan.maxGroupUser }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Máx. Caracteres (Texto)</span>
              <span class="meta-value">{{ plan.maxTextCharacters }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Máx. Caracteres (Notas)</span>
              <span class="meta-value">{{ plan.maxNoteCharacters }}</span>
            </div>

            <!-- ✅ NOVO: ficam como os dois últimos antes dos chips de funcionalidade -->
            <div class="meta-item">
              <span class="meta-label">Tipo de Uso</span>
              <span class="meta-value">{{ plan.usageType }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Max Contas Membros</span>
              <span class="meta-value">{{ plan.maxManagedAccounts }}</span>
            </div>
          </div>

          <!-- ✅ Todos os chips dentro do mesmo features-row -->
          <div class="features-row">
            <div
              :class="['feature-tag', plan.canExport ? 'active' : 'inactive']"
            >
              <i
                :class="[
 'fas',
                  plan.canExport ? 'fa-check-circle' : 'fa-times-circle',
                ]"
              ></i>
              Exportação
            </div>

            <div
              :class="[
 'feature-tag',
                plan.hasDashboard ? 'active' : 'inactive',
              ]"
            >
              <i
                :class="[
 'fas',
                  plan.hasDashboard ? 'fa-check-circle' : 'fa-times-circle',
                ]"
              ></i>
              Dashboard
            </div>

            <div
              :class="['feature-tag', plan.isActive ? 'active' : 'inactive']"
            >
              <i
                :class="[
 'fas',
                  plan.isActive ? 'fa-check-circle' : 'fa-times-circle',
                ]"
              ></i>
              Ativo
            </div>

            <!-- DEPOIS -->
            <div
              :class="['feature-tag', plan.isPublic ? 'active' : 'inactive']"
            >
              <i
                :class="[
 'fas',
                  plan.isPublic ? 'fa-check-circle' : 'fa-times-circle',
                ]"
              ></i>
              Público
            </div>
          </div>

          <hr class="content-separator" />

          <div class="footer-info">
            <div class="users-row">
              <div class="user-chip">
                <div class="chip-text">
                  <span class="chip-label">Criação</span>
                  <span class="chip-date">{{
                    formatDateTimeForDisplay(plan.createdAt)
                  }}</span>
                </div>
              </div>

              <div class="user-chip">
                <div class="chip-text">
                  <span class="chip-label">Última Atualização</span>
                  <span class="chip-date">{{
                    formatDateTimeForDisplay(plan.updatedAt)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes do plano...
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.plan-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  padding: 1rem;
}

.details-col {
  width: 100%;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.price-badge {
  background: var(--success-bg);
  color: var(--success-text);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

/* ✅ nowrap para manter todos os chips na mesma linha */
.features-row {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  margin-top: 6px;
  margin-bottom: 6px;
  overflow-x: auto;
}

.feature-tag {
  padding: 0.2rem 0.45rem;
  white-space: nowrap;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.feature-tag.active {
  background: var(--brand-soft);
  color: #3730a3;
}

.feature-tag.inactive {
  background: var(--neutral-100);
  color: var(--text-subtle);
  text-decoration: line-through;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.description-block {
  margin-bottom: 12px;
}

.description-text {
  margin: 6px 0 0 0;
}

.mp-plan-block {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mp-plan-value {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-dark);
  background: var(--surface-2);
  padding: 4px 8px;
  border-radius: 6px;
  word-break: break-all;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}
</style>